/* TemplateShowcase — the 10-card horizontal-snap scroller that lives on
   the Website product page (and could also drop into the marketing
   landing). Each card mirrors the studio template's signature mood:
   the previewColor strip up top, the niche tag in micro-caps, the
   tagline + description, and a "PREVIEW LIVE →" link that opens the
   founder's `/s/allure-image` rendered in that template_id via a
   `?preview=<id>` query string (consumed by the SitePicker route when
   we wire it; safe to leave as-is for now since unknown query strings
   are ignored).

   The scroller is horizontal-snap on mobile + desktop. A signature
   gradient mask on the right edge hints there's more to scroll
   without forcing a scroll bar. */

import React from "react";
import { TEMPLATES, TEMPLATE_IDS } from "@/templates/registry";

const NICHE_LINE = {
  editorial: "Studio · Editorial · Portraiture",
  minimal:   "Restrained · Curatorial · Print-first",
  bold:      "Type-led · Fashion · Creative Direction",
  journal:   "Documentary · Magazine · Travel",
};

export default function TemplateShowcase() {
  // We render every template in TEMPLATE_ORDER so the showcase always
  // stays in lockstep with the picker — adding a new template anywhere
  // surfaces it here automatically.
  return (
    <section className="relative py-20 lg:py-28 border-t border-b border-[color:var(--gold-foil)]/15 overflow-hidden" data-testid="marketing-template-showcase">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12 mb-10 lg:mb-14">
        <div className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--gold-foil)] mb-4">
          Chapter 02 · Templates
        </div>
        <h2 className="font-serif text-4xl lg:text-6xl font-light leading-[0.95] tracking-tight text-[color:var(--cream)] max-w-3xl">
          Ten templates.
          <br />
          <em className="text-[color:var(--gold-foil)]">One signature.</em>
        </h2>
        <p className="mt-5 text-[color:var(--cream)]/65 max-w-2xl leading-relaxed">
          Every template is a complete design system — chrome, typography, accent, mood — built to match a specific kind of photographer. Pick one in your admin and your entire public site shifts. Pick another tomorrow without losing a comma.
        </p>
      </div>

      {/* Horizontal-snap scroller */}
      <div
        className="overflow-x-auto overflow-y-hidden snap-x snap-mandatory pl-6 lg:pl-12 pb-6 -mb-6"
        style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(212,175,55,0.4) transparent" }}
      >
        <ul className="flex gap-5 pr-12 lg:pr-24" data-testid="template-showcase-scroller">
          {TEMPLATE_IDS.map((id, idx) => {
            const t = TEMPLATES[id];
            if (!t) return null;
            return (
              <li
                key={id}
                className="snap-start shrink-0 w-[280px] lg:w-[340px] flex flex-col border border-[color:var(--gold-foil)]/15 bg-[color:var(--charcoal)]/40 hover:border-[color:var(--gold-foil)]/40 transition-colors group"
                data-testid={`marketing-template-card-${id}`}
              >
                {/* Color strip — a faint mood-bar at the top */}
                <div className="h-1.5" style={{ background: t.previewColor }} />

                {/* Body */}
                <div className="p-6 lg:p-7 flex-1 flex flex-col">
                  <div className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--gold-foil)]/70 mb-3 font-mono">
                    {String(idx + 1).padStart(2, "0")} · {t.label}
                  </div>
                  <div className="font-serif text-3xl lg:text-4xl italic font-light leading-tight text-[color:var(--cream)] mb-3">
                    {t.label}.
                  </div>
                  {(t.niche || NICHE_LINE[id]) && (
                    <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--cream)]/55 mb-4">
                      {t.niche || NICHE_LINE[id]}
                    </div>
                  )}
                  <p className="text-sm text-[color:var(--cream)]/70 leading-relaxed flex-1">
                    {t.tagline}
                  </p>
                </div>

                {/* Mood signature row at bottom — color dot + accent name */}
                <div className="px-6 lg:px-7 pb-6 lg:pb-7 mt-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: t.previewColor }} />
                    <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--cream)]/45 font-mono">
                      Live
                    </span>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-foil)]/70 group-hover:text-[color:var(--gold-foil)] transition-colors">
                    Preview →
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Right-edge fade — hints at more to scroll */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-0 right-0 bottom-0 w-24 hidden md:block"
        style={{
          background: "linear-gradient(to left, var(--charcoal), transparent)",
        }}
      />
    </section>
  );
}
